import { DomPortal } from '@angular/cdk/portal';
import { computed, DestroyRef, effect, inject, signal, untracked, type Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  AnchoredOverlayService,
  type AnchoredOverlayDismissReason,
  type AnchoredOverlayRef,
  type AnchoredPlacement,
} from '../anchored-overlay';

export interface DisclosureFlyoutOverlayOptions {
  /** Element the panel is positioned against. Read once — the CDK binds its strategy to it. */
  anchor: () => HTMLElement;
  /** Structural rather than the panel component, so this stays a pure overlay-lifecycle helper. */
  panel: Signal<{ element: HTMLElement }>;
  expanded: Signal<boolean>;
  placement: Signal<AnchoredPlacement>;
  onDismiss: (reason: AnchoredOverlayDismissReason) => void;
}

export interface DisclosureFlyoutOverlayState {
  /** The placement in effect, which differs from the requested one after a collision flip. */
  placement: Signal<AnchoredPlacement>;
  /**
   * Whether the panel is in the overlay — which outlasts `expanded` by the length of the exit
   * animation. The panel has to stay rendered for that long, or it vanishes before the exit plays.
   */
  hosted: Signal<boolean>;
}

/**
 * Hosts a disclosure's panel in an anchored overlay while it is expanded, and reports the placement
 * actually in effect — which differs from the requested one after a collision flip, and is what the
 * panel needs to put its gap on the edge facing the trigger.
 *
 * `offset` is deliberately absent: the gap is the panel's own padding, so the two boxes stay in
 * contact for the pointer. See `DisclosureFlyoutPanelComponent`.
 *
 * Must be called from an injection context.
 */
export function useDisclosureFlyoutOverlay({
  anchor,
  panel,
  expanded,
  placement,
  onDismiss,
}: DisclosureFlyoutOverlayOptions): DisclosureFlyoutOverlayState {
  const anchoredOverlay = inject(AnchoredOverlayService);
  const destroyRef = inject(DestroyRef);

  const overlayRef = signal<AnchoredOverlayRef | undefined>(undefined);

  effect(() => {
    // Tracked, so re-aiming an open panel takes effect rather than waiting for the next open.
    const requestedPlacement = placement();
    const overlay = untracked(overlayRef);

    if (!expanded()) {
      overlay?.detach();

      return;
    }

    if (overlay) {
      overlay.updatePlacement(requestedPlacement);
      overlay.attach(new DomPortal(untracked(panel).element));

      return;
    }

    const created = anchoredOverlay.create({
      anchor: untracked(anchor),
      placement: requestedPlacement,
      // Keeps the panel a DOM descendant of the trigger's container, so Tab reaches it next.
      location: 'inline',
      motion: 'scale-fade',
      destroyRef,
    });

    created.dismissed.pipe(takeUntilDestroyed(destroyRef)).subscribe(onDismiss);
    created.attach(new DomPortal(untracked(panel).element));
    overlayRef.set(created);
  });

  // No post-render re-resolve, unlike `menu-overlay.ts`: that one attaches while its panel is still
  // hidden, so its first measurement is of an empty pane. This attaches only once expanded, and
  // Angular applies the panel's host bindings before the trigger's attach effect runs — the pane is
  // already at its full size when the CDK first measures it, and the collision flip lands on pass
  // one. Measured: first `getBoundingClientRect` of the pane reports the panel's real size, never 0.

  return {
    // Falls back to the requested placement until an overlay exists to resolve one.
    placement: computed(() => overlayRef()?.placement() ?? placement()),
    hosted: computed(() => overlayRef()?.attached() ?? false),
  };
}
